import { useOrdenCompraDetalle } from '@/hooks/useSolicitudesContado';
import { formatearCRC, formatearUSD } from '@adelante/shared';
import { ArrowSquareOut, CircleNotch } from '@phosphor-icons/react';

const monto = (m: number, moneda: string | null) =>
  moneda && moneda !== 'CRC' ? formatearUSD(m) : formatearCRC(m);

/** Detalle de una orden de compra de BC: obra, datos del encabezado y sus líneas. */
export function OrdenCompraDetalle({ numero }: { numero: string }) {
  const { data, isLoading, isError } = useOrdenCompraDetalle(numero);
  const d = data?.data;

  if (isLoading)
    return (
      <p className="flex items-center gap-2 py-2 text-sm text-muted-foreground">
        <CircleNotch className="h-4 w-4 animate-spin" weight="bold" aria-hidden="true" />
        Leyendo {numero} de Business Central…
      </p>
    );
  if (isError || !d)
    return (
      <p className="py-2 text-sm text-amber-700">
        No se pudo leer {numero}. Puede que ya no esté abierta en Business Central.
      </p>
    );

  const o = d.orden;
  return (
    <div className="space-y-3 text-sm">
      <dl className="grid grid-cols-2 gap-x-6 gap-y-1 sm:grid-cols-3">
        <div>
          <dt className="text-xs text-muted-foreground">Obra</dt>
          <dd className="font-medium">{o.obraCodigo ?? '—'}</dd>
        </div>
        <div>
          <dt className="text-xs text-muted-foreground">Proveedor</dt>
          <dd>{d.proveedor ?? o.vendorCode}</dd>
        </div>
        <div>
          <dt className="text-xs text-muted-foreground">Fecha · estado</dt>
          <dd>
            {o.fecha ?? '—'} · {o.estado}
          </dd>
        </div>
        <div>
          <dt className="text-xs text-muted-foreground">Total con impuesto</dt>
          <dd className="tabular-nums">{monto(o.monto, o.currencyCode)}</dd>
        </div>
        <div>
          <dt className="text-xs text-muted-foreground">Recibido sin facturar (₡)</dt>
          <dd className="tabular-nums">{formatearCRC(o.pendienteSinFacturarLCY)}</dd>
        </div>
        <div>
          <dt className="text-xs text-muted-foreground">Términos de pago</dt>
          <dd>{d.terminosPago ?? '—'}</dd>
        </div>
      </dl>

      {d.lineas.length === 0 ? (
        <p className="text-muted-foreground">La orden no tiene líneas.</p>
      ) : (
        <div className="max-h-56 overflow-auto rounded-md border">
          <table className="w-full text-xs">
            <thead className="sticky top-0 bg-muted text-left">
              <tr>
                <th className="px-2 py-1.5 font-medium">Descripción</th>
                <th className="px-2 py-1.5 text-right font-medium">Cant.</th>
                <th className="px-2 py-1.5 text-right font-medium">Costo unit.</th>
                <th className="px-2 py-1.5 text-right font-medium">Total</th>
              </tr>
            </thead>
            <tbody>
              {d.lineas.map((l) => (
                <tr key={l.numeroLinea} className="border-t">
                  <td className="px-2 py-1.5">
                    {l.descripcion}
                    {l.obraCodigo && (
                      <span className="ml-1 text-muted-foreground">({l.obraCodigo})</span>
                    )}
                  </td>
                  <td className="px-2 py-1.5 text-right tabular-nums">
                    {l.cantidad}
                    {l.unidad ? ` ${l.unidad}` : ''}
                    {l.cantidadRecibida > 0 && (
                      <div className="text-muted-foreground">recibido {l.cantidadRecibida}</div>
                    )}
                  </td>
                  <td className="px-2 py-1.5 text-right tabular-nums">
                    {monto(l.costoUnitario, o.currencyCode)}
                  </td>
                  <td className="px-2 py-1.5 text-right tabular-nums">
                    {monto(l.monto, o.currencyCode)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {d.linkBC && (
        <a
          href={d.linkBC}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-1 text-xs font-medium text-adelante-primary-dark underline-offset-2 hover:underline"
        >
          Abrir la orden en Business Central
          <ArrowSquareOut className="h-3.5 w-3.5" weight="bold" aria-hidden="true" />
        </a>
      )}
    </div>
  );
}
